import React, { useMemo, useState } from 'react';
import { toast, Skeleton } from '@ncoder/ui';
import { Database, Plus } from 'lucide-react';
import { Connection, ConnectionCard } from '@/components/atoms/connection/ConnectionCard';
import { AddConnectionDialog } from '@/components/molecules/connection/AddConnectionDialog';
import { EditConnectionDialog } from '@/components/molecules/connection/EditConnectionDialog';
import { PageHeader } from '@/components/molecules/PageHeader';
import { useOrg } from '@/hooks/org/useOrg';
import { useIsFetching } from '@tanstack/react-query';


export const ConnectionManager: React.FC = () => {
  const isLoading = useIsFetching({ queryKey: ['user-data'] }) > 0;
  const { organizations, selectedOrg, selectOrganization } = useOrg();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<{ id: string; name: string } | null>(null);

  React.useEffect(() => {
    if (!selectedOrg && organizations.length > 0) {
      selectOrganization(organizations[0].id);
    }
  }, [selectedOrg, organizations, selectOrganization]);

  const connections: Connection[] = organizations.map((org) => ({
    id: Number(org.id) || 0,
    orgId: org.id,
    name: org.orgName || org.salesforceOrgId || 'Unknown Org',
    url: org.instanceUrl || 'https://salesforce.com',
    status: 'connected' as const,
    lastSync: 'Just now',
    version: org.orgType || 'N/A',
    objects: org.metadataTypeCounts?.find((m) => m.metadataType === 'CustomObject')?.count || 0,
    apexClasses: org.metadataTypeCounts?.find((m) => m.metadataType === 'ApexClass')?.count || 0,
    apexTriggers: org.metadataTypeCounts?.find((m) => m.metadataType === 'ApexTrigger')?.count || 0,
    lightningComponents: org.metadataTypeCounts?.find((m) => m.metadataType === 'LightningComponentBundle')?.count || 0,
  }));

  const KEY_TYPES = [
    { key: 'ApexClass',                  label: 'Apex Classes' },
    { key: 'ApexTrigger',                label: 'Triggers'     },
    { key: 'CustomObject',               label: 'Objects'      },
    { key: 'LightningComponentBundle',   label: 'LWC'          },
    { key: 'Flow',                       label: 'Flows'        },
    { key: 'Profile',                    label: 'Profiles'     },
  ];

  const aggregatedMetadata = useMemo(() => {
    const map = new Map<string, number>();
    organizations.forEach((org) => {
      org.metadataTypeCounts?.forEach(({ metadataType, count }) => {
        map.set(metadataType, (map.get(metadataType) ?? 0) + count);
      });
    });
    return KEY_TYPES.map(({ key, label }) => ({
      label,
      count: map.get(key) ?? 0,
    }));
  }, [organizations]);

  const handleAddConnection = (name: string) => {
    toast({ title: "Connection Added", description: `Successfully connected to ${name}` });
  };

  const handleEditConnection = (orgId: string, currentName: string) => {
    setEditingOrg({ id: orgId, name: currentName });
    setEditDialogOpen(true);
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <PageHeader
        icon={Database}
        title="Connection Manager"
        description="Manage your Salesforce organization connections"
        actions={<AddConnectionDialog onAdd={handleAddConnection} />}
      />

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-4">
            {/* Org rows skeleton */}
            <div className="flex flex-col gap-1">
              <div className="mb-1 flex items-center gap-4 px-4">
                <div className="h-7 w-7 shrink-0" aria-hidden="true" />
                <Skeleton className="h-3 w-24 flex-1" />
                <div className="hidden shrink-0 items-center gap-6 md:flex">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-3 w-10" />)}
                </div>
                <div className="hidden w-80 shrink-0 md:block" aria-hidden="true" />
              </div>
              {[1, 2].map((i) => (
                <div key={i} className="flex items-center gap-4 rounded-lg border border-border/60 px-4 py-3">
                  <Skeleton className="h-7 w-7 rounded-md" />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Skeleton className="h-3.5 w-40" />
                    <Skeleton className="h-3 w-64" />
                  </div>
                  <div className="hidden items-center gap-6 pr-11 md:flex">
                    {[1, 2, 3].map((j) => <Skeleton key={j} className="h-4 w-10" />)}
                  </div>
                </div>
              ))}
            </div>

            {/* Metadata tiles skeleton */}
            <div className="flex flex-col gap-2">
              <Skeleton className="h-3 w-36" />
              <div className="grid grid-cols-6 gap-1.5">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex flex-col gap-2 rounded-lg border border-border/50 px-3 py-2.5">
                    <Skeleton className="h-4 w-10" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : connections.length === 0 ? (
          <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-border">
            <div className="text-center">
              <Plus className="mx-auto mb-2 h-5 w-5 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No organizations connected</p>
              <p className="mt-0.5 text-xs text-muted-foreground/60">Add a connection to get started</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Org list */}
            <div className="flex flex-col gap-1">
              <div className="mb-1 flex items-center gap-4 px-4">
                <div className="h-7 w-7 shrink-0" aria-hidden="true" />
                <span className="flex-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/50">
                  {connections.length} organization{connections.length !== 1 ? 's' : ''}
                </span>
                <div className="hidden shrink-0 items-center gap-6 md:flex">
                  {['Objects', 'Apex', 'LWC'].map((label) => (
                    <span key={label} className="w-10 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground/50">
                      {label}
                    </span>
                  ))}
                </div>
                <div className="hidden w-80 shrink-0 md:block" aria-hidden="true" />
              </div>

              {connections.map((connection) => (
                <ConnectionCard
                  key={connection.id}
                  connection={connection}
                  onEdit={handleEditConnection}
                />
              ))}
            </div>

            {/* Metadata breakdown — key types only */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 px-1">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/50">
                  Metadata breakdown
                </span>
                {organizations.length > 1 && (
                  <span className="font-mono text-[10px] text-muted-foreground/30">· across all orgs</span>
                )}
              </div>

              <div className="grid grid-cols-6 gap-1.5">
                {aggregatedMetadata.map(({ label, count }) => (
                  <div
                    key={label}
                    className="flex flex-col gap-1 rounded-lg border border-border/50 bg-card/30 px-3 py-2.5"
                  >
                    <span className="text-sm font-semibold tabular-nums text-foreground">
                      {count.toLocaleString()}
                    </span>
                    <span className="text-[10px] leading-tight text-muted-foreground">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {editingOrg && (
        <EditConnectionDialog
          orgId={editingOrg.id}
          currentName={editingOrg.name}
          isOpen={editDialogOpen}
          onOpenChange={setEditDialogOpen}
        />
      )}
    </div>
  );
};

export default ConnectionManager;
